import { type BookChapter } from './book';

// Markdown → clean chapters for audio. The point is that the synth should never read syntax aloud
// ("asterisk asterisk bold"), so all markup is stripped, leaving the words. Headings define chapters.

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
};

/** Remove inline markup (emphasis, code, links, images, html, entities), keeping readable text. */
export function stripInlineMarkdown(s: string): string {
  return (
    s
      .replace(/<(https?:\/\/[^>\s]+)>/g, '$1') // autolink <url> -> url
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // image -> alt text
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // inline link -> text
      .replace(/\[([^\]]+)\]\[[^\]]*\]/g, '$1') // reference link [text][id] -> text
      .replace(/\[\^[^\]]+\]/g, '') // footnote reference [^1]
      .replace(/`+([^`]+)`+/g, '$1') // inline code
      // Emphasis: the markers must hug the text (no inner-edge space), so "2 * 3" / "a _ b" survive.
      .replace(/\*\*\*(?=\S)([^*]+?)(?<=\S)\*\*\*/g, '$1')
      .replace(/\*\*(?=\S)([^*]+?)(?<=\S)\*\*/g, '$1')
      .replace(/\*(?=\S)([^*]+?)(?<=\S)\*/g, '$1')
      .replace(/(?<!\w)_{1,3}(?=\S)([^_]+?)(?<=\S)_{1,3}(?!\w)/g, '$1')
      .replace(/~~(?=\S)([^~]+?)(?<=\S)~~/g, '$1') // strikethrough
      .replace(/<\/?[a-zA-Z][^>]*>/g, '') // html tags
      .replace(/&(?:amp|lt|gt|quot|#39|apos|nbsp);/g, (m) => ENTITIES[m] ?? m) // entities
      .replace(/\|/g, ' ') // table cell separators read as nothing useful
  );
}

/** Drop a leading block marker (blockquote, list bullet/number, task checkbox) from a single line. */
function stripBlockPrefix(line: string): string {
  return line
    .replace(/^>+\s?/, '')
    .replace(/^([-*+]|\d+[.)])\s+/, '')
    .replace(/^\[[ xX]\]\s+/, ''); // task-list checkbox, after the bullet is removed
}

const HEADING = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/;
const FENCE = /^\s*(```|~~~)/;
const HR = /^\s*([-*_]\s*){3,}$/;
const SETEXT_UNDERLINE = /^\s*=+\s*$/; // '---' is already caught by HR
const TABLE_SEPARATOR = /^\s*\|?[\s:|-]+\|[\s:|-]*$/;
const FOOTNOTE_DEF = /^\s*\[\^[^\]]+\]:/;

/**
 * Parse markdown into chapters (one per heading) of clean paragraphs. Properly-closed fenced code
 * blocks are dropped (code read aloud is noise); an unterminated fence is treated as literal text so
 * one stray ``` never silences the rest of the book. Returns the first H1 as the title.
 */
export function parseMarkdown(text: string): { title: string | null; chapters: BookChapter[] } {
  const lines = text.split(/\r?\n/);
  const chapters: BookChapter[] = [];
  let current: BookChapter | null = null;
  let buffer: string[] = [];
  let title: string | null = null;
  let chapterIndex = 0;
  let inFence = false;
  let fenceLines: string[] = [];

  const flushParagraph = (): void => {
    if (buffer.length === 0) return;
    const clean = stripInlineMarkdown(buffer.join(' ')).replace(/\s+/g, ' ').trim();
    buffer = [];
    if (!clean) return;
    if (!current) {
      current = { href: `md-${chapterIndex++}`, label: title ?? '', paragraphs: [] };
      chapters.push(current);
    }
    current.paragraphs.push(clean);
  };

  const consume = (line: string): void => {
    if (HR.test(line) || SETEXT_UNDERLINE.test(line) || TABLE_SEPARATOR.test(line)) return;
    if (FOOTNOTE_DEF.test(line)) return;
    if (line.trim() === '') {
      flushParagraph();
      return;
    }
    buffer.push(stripBlockPrefix(line.trim()));
  };

  for (const line of lines) {
    if (FENCE.test(line)) {
      if (inFence) {
        inFence = false;
        fenceLines = []; // properly closed: it was code, drop it
      } else {
        inFence = true;
        fenceLines = [];
      }
      continue;
    }
    if (inFence) {
      fenceLines.push(line);
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      const label = stripInlineMarkdown(heading[2]).trim();
      if (heading[1].length === 1 && !title) title = label;
      current = { href: `md-${chapterIndex++}`, label: label || 'Chapter', paragraphs: [] };
      chapters.push(current);
      continue;
    }
    consume(line);
  }

  // An opening fence that never closed wasn't really code — read its lines as text rather than
  // dropping the tail of the document.
  if (inFence) for (const line of fenceLines) consume(line);
  flushParagraph();

  return { title, chapters: chapters.filter((c) => c.paragraphs.length > 0) };
}
